import { message, Modal } from 'antd';
import { ILineResource } from '../../entities/farms.entities';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Subtitle,
} from '../shared';
import { translate } from '../../lib/lang.helper';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { SelectTagsByFarmID } from '../../store/extra/extra.selector';
import { useState } from 'react';
import moment from 'moment';
import { sendSingleRequest } from '../../apis';
import { loadTags } from '../../store/automation/automation.actions';
import { loadExtraData } from '../../store/extra/extra.actions';
import { loadFarmsData } from '../../store/farms/farms.actions';

interface IPastForm {
  tag_id: number | null;
  start_time: number;
  end_time?: number | null;
}

interface Props {
  visible: boolean;
  lineData: ILineResource;
  onClose: () => void;
  onChange?: () => void;
  onComplete?: () => void;
}

const PastTagModal = ({
  visible,
  onClose,
  onChange,
  onComplete,
  lineData,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const lineTags = SelectTagsByFarmID(lineData.farm_id);

  const [pastForm, setPastForm] = useState<IPastForm>({
    tag_id: null,
    start_time: Date.now(),
  });
  const [disabled, setDisabled] = useState(false);

  const confirmClick = async () => {
    if (!pastForm.tag_id) {
      message.error(translate(lang, 'Please select a tag'));
      return;
    }
    const data = {
      line_id: lineData.id,
      tag_id: pastForm.tag_id,
      start_time: moment(pastForm.start_time).format('YYYY-MM-DD'),
      end_time: pastForm.end_time
        ? moment(pastForm.end_time).format('YYYY-MM-DD')
        : null,
    };
    setDisabled(true);
    const res = await sendSingleRequest(data, 'POST', 'api/tag_line', true);
    setDisabled(false);
    if (res.status) {
      await dispatch(loadTags());
      if (onChange) {
        await onChange();
      }
      dispatch(loadExtraData());
      dispatch(loadFarmsData());
      if (onComplete) {
        onComplete();
      }
      onClose();
    } else {
      message.error(translate(lang, res.data?.message ?? 'Unknown error'));
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {`${lineData.farm_name} - ${lineData.line_name}: `}
            {translate(lang, 'Add tag in the past')}
          </Subtitle>
        </div>
        <div className='mb-17'>
          <Dropdown
            label={translate(lang, 'Select Tag')}
            options={
              lineTags?.map(x => ({
                id: x.id.toString(),
                value: x.id.toString(),
                label: x.name,
              })) ?? []
            }
            value={pastForm.tag_id?.toString() ?? undefined}
            onChange={v => setPastForm({ ...pastForm, tag_id: Number(v) })}
          />
        </div>
        <div className='mb-17'>
          <Datepicker
            label={translate(lang, 'Start time')}
            defaultValue={pastForm.start_time}
            onChange={e =>
              e &&
              setPastForm({ ...pastForm, start_time: e.toDate().getTime() })
            }
          />
        </div>
        <div className='mb-17'>
          <CheckboxButton
            label={translate(lang, '_tag_still_active')}
            checked={!pastForm.end_time}
            onChange={e => {
              e.target.checked
                ? setPastForm({
                    ...pastForm,
                    end_time: null,
                  })
                : setPastForm({
                    ...pastForm,
                    end_time: Date.now(),
                  });
            }}
          />
        </div>
        {!!pastForm.end_time && (
          <div className='mb-17'>
            <Datepicker
              label={translate(lang, 'End time')}
              defaultValue={pastForm.end_time}
              onChange={e =>
                e &&
                setPastForm({ ...pastForm, end_time: e.toDate().getTime() })
              }
            />
          </div>
        )}
      </div>
      <div className='modal-button d-flex justify-end mt-24'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color={'green'}
          className='rsp-btn ml-16'
          onClick={confirmClick}
          disabled={disabled}
        >
          {translate(lang, onComplete ? 'Complete' : 'Confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default PastTagModal;
